import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import capitalize from 'lodash/capitalize'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import api from 'api'
import { useUpdateAccountMutation } from 'api/accounts/updateAccount'
import {
  CACHE_TAG_ACCOUNT_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_PAYMENT_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
  CACHE_TAG_BILLING_PROFILE_VERSION_LIST,
  CACHE_TAG_WORK_ORDER_LIST,
  CACHE_TAG_WORK_ORDER_LIST_SUMMARY,
} from 'api/cacheTagTypes'
import { useLazyGetBillingProfileVersionsQuery } from 'api/entity-version/entityVersionCrud'
import CancelButton from 'components/buttons/CancelButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { getCustomerDetails } from 'middleware/actions/customers'
import { putIsLoading } from 'middleware/actions/response'
import T from 'T'
import { getExistingCustomerInfo, getPOAndTaxInfo } from 'utils/customerPayload'
import { handleError } from 'utils/error'
import { addBillingPOAndTax } from 'utils/form-body'
import { get } from 'utils/lodash'

import BillingProfileDropdown from '../../common/billing-dropdown/index'
import RegenerateDraftInvoicesDialog from '../common/RegenerateDraftInvoicesDialog'
import SettingsHistoryAccordion from '../common/SettingsHistoryAccordion'
import { useRegenerateDraftInvoicesPrompt } from '../common/useRegenerateDraftInvoicesPrompt'
import useVersionsState from '../common/useVersionsState'

const EditBillingProfileDialog = ({ isOpen = false, customerDetails = {}, onClose }) => {
  const dispatch = useDispatch()
  const [updateAccount] = useUpdateAccountMutation()
  const [billingProfileId, setBillingProfileId] = useState('')
  const [getVersions, { data: versionsData, isLoading: isVersionsLoading, isFetching: isVersionsFetching, isSuccess: isVersionsSuccess }] =
    useLazyGetBillingProfileVersionsQuery()

  const accountId = get(customerDetails, 'id')
  const accountBilling = get(customerDetails, 'accountBilling', {})
  const currentBillingProfileId = get(accountBilling, 'billingProfile.id')

  const { maybePromptRegenerate, regenerateDraftDialogProps } = useRegenerateDraftInvoicesPrompt(accountId)

  const {
    versions: allVersions,
    hasVersions,
    versionsLoading,
  } = useVersionsState({
    versionsData,
    isVersionsLoading,
    isFetching: isVersionsFetching,
    isVersionsSuccess,
  })

  const versions = useMemo(() => {
    return allVersions.filter((v, i) => {
      const prev = allVersions[i - 1]
      return prev && prev.billingProfileName !== v.billingProfileName
    })
  }, [allVersions])

  const handleBillingProfileChange = useCallback(event => setBillingProfileId(event.target.value), [])

  const handleUpdateBillingProfile = useCallback(() => {
    dispatch(putIsLoading(true))
    const payload = {
      id: accountId,
      ...getExistingCustomerInfo(customerDetails),
      accountBilling: {
        billingProfileId,
        id: accountBilling.id,
        ...addBillingPOAndTax({ ...getPOAndTaxInfo(customerDetails) }),
      },
    }

    updateAccount(payload)
      .unwrap()
      .then(async () => {
        dispatch(getCustomerDetails({ accountId }))
        dispatch(
          api.util.invalidateTags([
            CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
            CACHE_TAG_ACCOUNT_INVOICE_LIST,
            CACHE_TAG_ACCOUNT_PAYMENT_LIST,
            CACHE_TAG_WORK_ORDER_LIST,
            CACHE_TAG_WORK_ORDER_LIST_SUMMARY,
            { type: CACHE_TAG_BILLING_PROFILE_VERSION_LIST, id: accountId },
          ])
        )
        toast.success(T.BILLING_PROFILE_UPDATED_SUCCESSFULLY)
        onClose()
        // The new billing profile may change the account's due-date config; offer to regenerate drafts.
        await maybePromptRegenerate()
      })
      .catch(handleError)
      .finally(() => dispatch(putIsLoading(false)))
  }, [accountBilling, accountId, billingProfileId, customerDetails, dispatch, maybePromptRegenerate, onClose, updateAccount])

  const getVersionChangeLines = useCallback(version => {
    return [`${T.BILLING_PROFILE}: ${version.billingProfileName}`]
  }, [])

  useEffect(() => {
    if (isOpen) {
      setBillingProfileId(currentBillingProfileId)
      if (accountId) {
        getVersions({ accountId }).unwrap().catch(handleError)
      }
    }
  }, [isOpen, accountId, currentBillingProfileId, getVersions])

  return (
    <>
      <HHBaseDialog
        fullWidth
        open={isOpen}
        sx={{
          '& .MuiDialog-container .MuiPaper-root': {
            maxWidth: '625px',
          },
        }}
        onClose={onClose}
      >
        <HHDialogTitle title={capitalize(T.BILLING_PROFILE)} onClose={onClose} />
        <DialogContent>
          <Box mt={2}>
            <BillingProfileDropdown
              billingProfileId={billingProfileId}
              label={`${T.BILLING_PROFILE} *`}
              onChange={handleBillingProfileChange}
            />
            {billingProfileId !== currentBillingProfileId && (
              <Alert severity="error" sx={{ mt: 2, alignItems: 'center' }}>
                {T.BILLING_PROFILE_CHANGE_WARNING}
              </Alert>
            )}
          </Box>
          <SettingsHistoryAccordion
            getVersionChangeLines={getVersionChangeLines}
            hasVersions={versions.length > 0 && hasVersions}
            versions={versions}
            versionsLoading={versionsLoading}
          />
        </DialogContent>
        <HHDialogActions>
          <CancelButton size="medium" onClick={onClose} />
          <Button
            disabled={!billingProfileId || billingProfileId === currentBillingProfileId}
            size="small"
            variant="contained"
            onClick={handleUpdateBillingProfile}
          >
            {T.SAVE}
          </Button>
        </HHDialogActions>
      </HHBaseDialog>
      <RegenerateDraftInvoicesDialog {...regenerateDraftDialogProps} />
    </>
  )
}

EditBillingProfileDialog.propTypes = {
  isOpen: PropTypes.bool,
  customerDetails: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

export default EditBillingProfileDialog
